import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  FormGroup,
} from "reactstrap";
import { getSpaPackages, saveSpaPackage, checkSpaPackageBillingUsage, exportSpaPackagesToExcel, exportSpaPackageDetailsToExcel, bulkImportSpaPackages } from "../../components/Api/GmsSpaPackage";
import { getSpaServices } from "../../components/Api/GmsSpaService";
import { getTaxes } from "../../components/Api/GmsTax";
// import { getProducts } from "../../components/Api/GmsProduct"; // COMMENTED OUT: Product functionality temporarily disabled
import { Eye, SquarePen, Plus, Minus, Download, Upload } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import * as XLSX from 'xlsx';
import Pagination from "rc-pagination";
import 'rc-pagination/assets/index.css';
import { 
  hasPagePermissionWithAdmin, 
  hasViewOnlyPermission, 
  hasEditOrHigherPermission,
  hasDeletePermissionWithAdmin,
  isUserAdmin 
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";

// Custom styles for Select components
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

const defaultForm = {
  PackageId: 0,
  PackageCode: "",
  PackageName_En: "",
  PackageName_Ar: "",
  Cost: null,
  Amount: null,
  DiscountAmount: 0,
  TaxAmount: 0,
  NetAmount: 0,
  Status: true,
  CreatedBy: 1,
  ModifiedBy: 1,
  Details: [],
  TaxIds: [],
  DiscountPercentage: null,
};

const emptyFilter = {
  PackageId: null,
  PackageCode: "",
  PackageName_En: "",
  Status: null,
};

const defaultServiceDetail = {
  ItemType: "Service", // "Service" or "Product"
  Service_Id: null,
  // Product_Id: null, // COMMENTED OUT: Product functionality temporarily disabled
  Quantity: 1,
};

export default function SpaPackagesPage() {
  // Check permissions first
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsSpaPackages');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsSpaPackages');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsSpaPackages');
  const hasDeletePermission = hasDeletePermissionWithAdmin(permissions, '/GmsSpaPackages');
  const isAdmin = isUserAdmin();

  const [spaPackages, setSpaPackages] = useState([]);
  const [spaServices, setSpaServices] = useState([]);
  // const [products, setProducts] = useState([]); // COMMENTED OUT: Product functionality temporarily disabled
  const [taxes, setTaxes] = useState([]);
  const [form, setForm] = useState(() => {
    // Set default status based on permissions
    const defaultStatus = hasDeletePermission ? defaultForm.Status : true;
    return { ...defaultForm, Status: defaultStatus };
  });
  const [filter, setFilter] = useState(emptyFilter);
  const [serviceDetails, setServiceDetails] = useState([defaultServiceDetail]);
  const [selectedTaxes, setSelectedTaxes] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [editLoadingRow, setEditLoadingRow] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [activeTab, setActiveTab] = useState("1");
  const [hasAppliedFilter, setHasAppliedFilter] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [isUsedInBilling, setIsUsedInBilling] = useState(false);
  const [originalServiceDetails, setOriginalServiceDetails] = useState([]);
  const [billingUsageMap, setBillingUsageMap] = useState({});
  const [formKey, setFormKey] = useState(0);

  // Excel import states
  const [excelFile, setExcelFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0 });
  const [importResult, setImportResult] = useState(null);

  // Session management
  const getUserSession = () => {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
    return {
      userId: authUser.userId || 1,
      userName: authUser.userName || "System",
    };
  };

  useEffect(() => {
    console.log('Component mounted');
    if (hasPermission) {
      console.log('Loading data due to permission');
      loadSpaServices(); // Load spa services first
      // loadProducts(); // Load products // COMMENTED OUT: Product functionality temporarily disabled
      loadTaxes(); // Load taxes
      loadSpaPackages(); // Load spa packages on component mount
      
      // Set default tab based on permissions
      if (isViewOnly) {
        setActiveTab("2"); // Show list tab for view-only users
      }
    } else {
      console.log('No permission to view page');
      window.location.href = '/Dashboard';
    }
  }, [hasPermission, isViewOnly]);

  useEffect(() => {
    if (hasAppliedFilter) {
      loadSpaPackages();
      setHasAppliedFilter(false);
    }
  }, [hasAppliedFilter]);



  // Calculate discount amount when amount or discount percentage changes
  useEffect(() => {
    calculateDiscountAmount();
  }, [form.Amount, form.DiscountPercentage]);

  const loadSpaServices = async () => {
    try {
      const data = await getSpaServices({ Status: true }); // Only active services
      setSpaServices(data || []);
    } catch (error) {
      console.error("Error loading spa services:", error);
      Swal.fire("Error", "Failed to load spa services", "error");
    }
  };

  // COMMENTED OUT: Product functionality temporarily disabled
  // const loadProducts = async () => {
  //   try {
  //     const data = await getProducts({ Product_Status: true }); // Only active products
  //     setProducts(data || []);
  //   } catch (error) {
  //     console.error("Error loading products:", error);
  //     Swal.fire("Error", "Failed to load products", "error");
  //   }
  // };

  const loadTaxes = async () => {
    try {
      const data = await getTaxes({ tax_Active: true }); // Only active taxes
      setTaxes(data || []);
    } catch (error) {
      console.error("Error loading taxes:", error);
      Swal.fire("Error", "Failed to load taxes", "error");
    }
  };

  const loadSpaPackages = async () => {
    try {
      console.log("=== LOADING SPA PACKAGES ===");
      console.log("Filter:", filter);
      setLoading(true);
      const data = await getSpaPackages(filter);
      console.log("Loaded spa packages data:", data);
      
      // Ensure all numeric fields have default values to prevent toFixed errors
      const processedData = (data || []).map(item => {
        // Convert all numeric fields to numbers and provide defaults
        const cost = parseFloat(item.cost) || 0;
        const amount = parseFloat(item.amount) || 0;
        const discountAmount = parseFloat(item.discountAmount) || 0;
        const taxAmount = parseFloat(item.taxAmount) || 0;
        const netAmount = parseFloat(item.netAmount) || 0;
        
        return {
          ...item,
          PackageId: item.packageId,
          PackageCode: item.packageCode,
          PackageName_En: item.packageName_En,
          PackageName_Ar: item.packageName_Ar,
          Cost: cost,
          Amount: amount,
          DiscountAmount: discountAmount,
          DiscountPercentage: parseFloat(item.discountPercentage) || 0,
          TaxAmount: taxAmount,
          NetAmount: netAmount,
          Status: item.status,
          ServiceCount: item.serviceCount,
          CreatedOn: item.createdOn,
          CreatedBy: item.createdBy,
          ModifiedOn: item.modifiedOn,
          ModifiedBy: item.modifiedBy,
          Details: item.details,
          TaxIds: item.taxIds,
        };
      });
      
      setSpaPackages(processedData);
      setTotalCount(processedData.length);
      console.log("Total count set to:", processedData.length);
      
      // Check billing usage for all packages
      processedData.forEach(async (pkg) => {
        const packageId = pkg.PackageId || pkg.packageId;
        if (!billingUsageMap[packageId]) {
          await checkBillingUsageForPackage(packageId);
        }
      });
    } catch (error) {
      console.error("Error loading spa packages:", error);
      Swal.fire("Error", "Failed to load spa packages", "error");
    } finally {
      setLoading(false);
    }
  };

  const calculateDiscountAmount = () => {
    const baseAmount = parseFloat(form.Amount) || 0;
    const discountPercentage = parseFloat(form.DiscountPercentage) || 0;
    
    // Calculate discount amount from percentage
    const discountAmount = (baseAmount * discountPercentage) / 100;

    setForm(prev => ({
      ...prev,
      DiscountAmount: parseFloat(discountAmount.toFixed(2)),
      TaxAmount: 0,
      NetAmount: 0,
    }));
  };

  // Calculate Net Amount: Amount - (Amount * Discount%) + (Amount after discount * Tax%)
  const calculateNetAmount = (amount, discountPercentage, selectedTaxes) => {
    if (!amount || amount <= 0) return 0;
    
    const discountPercent = discountPercentage || 0;
    const discountAmount = amount * discountPercent / 100;
    const amountAfterDiscount = amount - discountAmount;
    
    const taxPercentage = selectedTaxes.reduce((total, tax) => {
      const taxData = taxes.find(t => t.tax_Id === tax.value);
      return total + (taxData ? taxData.tax_Perc : 0);
    }, 0);
    const taxAmount = amountAfterDiscount * taxPercentage / 100;
    
    return amountAfterDiscount + taxAmount;
  };

  const handleSave = async () => {
    try {
      console.log("=== SAVE PROCESS STARTED ===");
      console.log("Form data:", form);
      console.log("Service details:", serviceDetails);
      console.log("Selected taxes:", selectedTaxes);
      
      // Validation
      if (!form.PackageName_En.trim()) {
        Swal.fire("Error", "Package Name (English) is required", "error");
        return;
      }

      // Validate Cost Price
      if (!form.Cost || form.Cost <= 0) {
        Swal.fire("Error", "Cost Price must be greater than 0", "error");
        return;
      }

      // Validate Sales Price
      if (!form.Amount || form.Amount <= 0) {
        Swal.fire("Error", "Sales Price must be greater than 0", "error");
        return;
      }

      // Validate service/product details haven't changed if package is used in billing
      if (isUsedInBilling && isEditing) {
        const currentDetails = serviceDetails.map(detail => ({
          Service_Id: detail.Service_Id,
          // Product_Id: detail.Product_Id, // COMMENTED OUT: Product functionality temporarily disabled
          Quantity: detail.Quantity
        }));
        
        const originalDetails = originalServiceDetails.map(detail => ({
          Service_Id: detail.Service_Id,
          // Product_Id: detail.Product_Id, // COMMENTED OUT: Product functionality temporarily disabled
          Quantity: detail.Quantity
        }));
        
        const detailsChanged = JSON.stringify(currentDetails.sort()) !== JSON.stringify(originalDetails.sort());
        
        if (detailsChanged) {
          Swal.fire({
            title: "Validation Error",
            text: "Services cannot be modified for packages that have been used in billing. Please restore the original selection.", // UPDATED: Removed Products from message
            icon: "error",
            confirmButtonText: "OK"
          });
          return;
        }
      }

      if (!form.PackageCode.trim()) {
        Swal.fire("Error", "Package Code is required", "error");
        return;
      }

      // Validate that at least one service or product is selected
      const hasValidDetails = serviceDetails.some(detail => 
        (detail.Service_Id && detail.Quantity > 0) // || (detail.Product_Id && detail.Quantity > 0) // COMMENTED OUT: Product functionality temporarily disabled
      );
      
      if (serviceDetails.length === 0 || !hasValidDetails) {
        Swal.fire("Error", "At least one service must be selected", "error"); // UPDATED: Removed products from message
        return;
      }

      // Check for empty quantity fields
      const emptyQuantityDetails = serviceDetails.filter(detail => 
        (detail.Service_Id) && (!detail.Quantity || detail.Quantity === '') // || detail.Product_Id // COMMENTED OUT: Product functionality temporarily disabled
      );
      if (emptyQuantityDetails.length > 0) {
        Swal.fire("Error", "Please enter quantity for all selected services", "error"); // UPDATED: Removed products from message
        return;
      }

      // Validate that each detail has either Service_Id or Product_Id (not both, not neither)
      const invalidDetails = serviceDetails.filter(detail => 
        !detail.Service_Id // || (detail.Service_Id && detail.Product_Id) || (!detail.Service_Id && !detail.Product_Id) // COMMENTED OUT: Product functionality temporarily disabled
      );
      if (invalidDetails.length > 0) {
        Swal.fire("Error", "Each item must have a Service selected", "error"); // UPDATED: Removed products from message
        return;
      }

      setSaveLoading(true);
      const session = getUserSession();
      console.log("User session:", session);
      
      // Prepare service/product details - map to correct API format
      const validServiceDetails = serviceDetails
        .filter(detail => (detail.Service_Id) && detail.Quantity > 0) // || detail.Product_Id // COMMENTED OUT: Product functionality temporarily disabled
        .map(detail => ({
          Service_Id: detail.Service_Id || null,
          // Product_Id: detail.Product_Id || null, // COMMENTED OUT: Product functionality temporarily disabled
          Quantity: detail.Quantity,
          Package_Id: form.PackageId || 0
        }));
      console.log("Valid service details:", validServiceDetails); // UPDATED: Removed products from log message
      
      // Calculate Net Amount in frontend
      const calculatedNetAmount = calculateNetAmount(form.Amount, form.DiscountPercentage || 0, selectedTaxes);

      // Prepare save data - include dates
      // Ensure DiscountPercentage is 0 if null/empty
      const discountPercentage = form.DiscountPercentage || 0;
      
      const saveData = {
        ...form,
        Cost: form.Cost || 0,
        Amount: form.Amount || 0,
        DiscountPercentage: discountPercentage,
        NetAmount: calculatedNetAmount,
        CreatedBy: isEditing ? form.CreatedBy : session.userId,
        ModifiedBy: session.userId,
        CreatedOn: isEditing ? form.CreatedOn : new Date().toISOString(),
        ModifiedOn: new Date().toISOString(),
        Details: validServiceDetails,
        TaxIds: selectedTaxes.map(tax => tax.value),
      };

      console.log("Final save data being sent to API:", saveData);

      const result = await saveSpaPackage(saveData);
      console.log("API response:", result);
      
      // Check if save was successful based on API response
      if (result && (result.PackageId || result.packageId || result.id || result === "" || result.length === 0)) {
        Swal.fire("Success", "Spa Package saved successfully!", "success");
      } else {
        console.warn("Unexpected API response format:", result);
        Swal.fire("Success", "Spa Package saved successfully!", "success");
      }
      
      // Reset form and reload data
      handleClear();
      loadSpaPackages();
      // Stay on Add form tab (tab "1") instead of switching to list tab
    } catch (error) {
      console.error("Error saving spa package:", error);
      console.error("Error details:", error.response);
      let errorMessage = "Failed to save spa package";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (typeof error.response?.data === 'string') {
        // Extract error message from exception string
        const match = error.response.data.match(/System\.InvalidOperationException:\s*(.+?)(?:\s+at\s+|$)/);
        errorMessage = match ? match[1].trim() : error.response.data;
      }
      Swal.fire("Error", errorMessage, "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const checkBillingUsageForPackage = async (packageId) => {
    try {
      console.log(`Checking billing usage for package ${packageId}...`);
      const billingUsageCheck = await checkSpaPackageBillingUsage(packageId);
      console.log(`Billing usage result for package ${packageId}:`, billingUsageCheck);
      
      setBillingUsageMap(prev => {
        const newMap = {
          ...prev,
          [packageId]: billingUsageCheck.isUsedInBilling
        };
        console.log("Updated billing usage map:", newMap);
        return newMap;
      });
      
      return billingUsageCheck.isUsedInBilling;
    } catch (error) {
      console.error(`Error checking billing usage for package ${packageId}:`, error);
      return false;
    }
  };

  const handleEdit = async (item) => {
    try {
      setEditLoadingRow(item.PackageId || item.packageId);
      
      // Check if package is used in billing
      const isUsed = await checkBillingUsageForPackage(item.PackageId || item.packageId);
      setIsUsedInBilling(isUsed);
      
      if (isUsed) {
        Swal.fire({
          title: "Warning",
          text: "This package has been used in billing. Only amount, discount, and tax can be modified. Package Code, Package Name, and Services cannot be changed.", // UPDATED: Removed Products from message
          icon: "warning",
          confirmButtonText: "Continue"
        });
      }
      
      // Keep all fields including dates
      const otherFields = item;

    setForm({
      PackageId: otherFields.PackageId || otherFields.packageId,
      PackageCode: otherFields.PackageCode || otherFields.packageCode,
      PackageName_En: otherFields.PackageName_En || otherFields.packageName_En,
      PackageName_Ar: (otherFields.PackageName_Ar || otherFields.packageName_Ar) || "",
      Cost: otherFields.Cost || otherFields.cost || null,
      Amount: otherFields.Amount || otherFields.amount || null,
      DiscountAmount: otherFields.DiscountAmount || otherFields.discountAmount || 0,
      DiscountPercentage: otherFields.DiscountPercentage !== null && otherFields.DiscountPercentage !== undefined 
        ? (otherFields.DiscountPercentage || otherFields.discountPercentage) 
        : null,
      TaxAmount: otherFields.TaxAmount || otherFields.taxAmount || 0,
      NetAmount: otherFields.NetAmount || otherFields.netAmount || 0,
      Status: otherFields.Status !== undefined ? otherFields.Status : otherFields.status,
      CreatedBy: otherFields.CreatedBy || otherFields.createdBy,
      CreatedOn: otherFields.CreatedOn || otherFields.createdOn,
      ModifiedOn: otherFields.ModifiedOn || otherFields.modifiedOn,
      ModifiedBy: otherFields.ModifiedBy || otherFields.modifiedBy,
    });

    // Set service/product details
    const packageServiceDetails = otherFields.Details || otherFields.details || [];
    if (packageServiceDetails.length > 0) {
      setServiceDetails(packageServiceDetails.map(detail => ({
        ItemType: "Service", // detail.Product_Id || detail.product_Id ? "Product" : "Service", // COMMENTED OUT: Product functionality temporarily disabled
        Service_Id: detail.Service_Id || detail.service_Id || null,
        // Product_Id: detail.Product_Id || detail.product_Id || null, // COMMENTED OUT: Product functionality temporarily disabled
        Quantity: detail.Quantity || detail.quantity,
      })));
    } else {
      setServiceDetails([defaultServiceDetail]);
    }

    // Set selected taxes
    const packageTaxIds = otherFields.TaxIds || otherFields.taxIds || [];
    if (packageTaxIds.length > 0) {
      const selectedTaxOptions = taxOptions.filter(tax => 
        packageTaxIds.includes(tax.value)
      );
      setSelectedTaxes(selectedTaxOptions);
    } else {
      setSelectedTaxes([]);
    }

    setIsEditing(true);
    setActiveTab("1");
    
    // Store original service/product details for comparison
    const serviceDetails = otherFields.Details || otherFields.details || [];
    setOriginalServiceDetails(serviceDetails.map(detail => ({
      Service_Id: detail.Service_Id || detail.service_Id || null,
      // Product_Id: detail.Product_Id || detail.product_Id || null, // COMMENTED OUT: Product functionality temporarily disabled
      Quantity: detail.Quantity || detail.quantity,
    })));
    
    } catch (error) {
      console.error("Error in handleEdit:", error);
      Swal.fire("Error", "Failed to load package for editing", "error");
    } finally {
      setEditLoadingRow(null);
    }
  };

  const handleView = async (item) => {
    try {
      const details = item.Details || item.details;
      const taxIds = item.TaxIds || item.taxIds;
      
      // Get service/product names for display
      const itemNames = details?.map(detail => {
        const serviceId = detail.Service_Id || detail.service_Id;
        // const productId = detail.Product_Id || detail.product_Id; // COMMENTED OUT: Product functionality temporarily disabled
        const quantity = detail.Quantity || detail.quantity;
        
        if (serviceId) {
          const service = spaServices.find(s => s.spaService_Id === serviceId);
          return service ? `${service.spaServiceName_En} (Service, Qty: ${quantity})` : `Service ID: ${serviceId} (Qty: ${quantity})`;
        }
        // COMMENTED OUT: Product functionality temporarily disabled
        // else if (productId) {
        //   const product = products.find(p => p.product_Id === productId);
        //   return product ? `${product.product_NameEn} (Product, Qty: ${quantity})` : `Product ID: ${productId} (Qty: ${quantity})`;
        // }
        return '';
      }).filter(name => name).join(', ') || 'No services'; // UPDATED: Removed products from message

      // Get tax names for display
      const taxNames = taxIds?.map(taxId => {
        const tax = taxes.find(t => t.tax_Id === taxId);
        return tax ? `${tax.tax_NameEn} (${tax.tax_Perc}%)` : `Tax ID: ${taxId}`;
      }).join(', ') || 'No taxes';

      const packageId = item.PackageId || item.packageId || item.Package_Id || item.package_Id || 0;
      
      Swal.fire({
        title: `<div style="text-align: center; color: #556ee6; font-size: 24px; font-weight: bold; margin-bottom: 10px;">
                  <i class="bx bx-spa" style="margin-right: 10px;"></i>
                  Spa Package Details
                </div>`,
        html: `
          <div style="max-width: 700px; margin: 0 auto;">
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px;">
              <div style="background: #f8f9fa; padding: 15px; border-radius: 8px; border-left: 4px solid #556ee6;">
                <h6 style="color: #556ee6; margin-bottom: 10px; font-weight: bold;">
                  <i class="bx bx-info-circle" style="margin-right: 5px;"></i>
                  Basic Information
                </h6>
                <div style="margin-bottom: 8px;">
                  <strong style="color: #495057;">Package Code:</strong>
                  <span style="color: #6c757d; margin-left: 5px;">${item.PackageCode || item.packageCode}</span>
                </div>
                <div style="margin-bottom: 8px;">
                  <strong style="color: #495057;">Package Name (English):</strong>
                  <span style="color: #6c757d; margin-left: 5px;">${item.PackageName_En || item.packageName_En}</span>
                </div>
                <div style="margin-bottom: 8px;">
                  <strong style="color: #495057;">Package Name (Arabic):</strong>
                  <span style="color: #6c757d; margin-left: 5px;">${(item.PackageName_Ar || item.packageName_Ar) || 'N/A'}</span>
                </div>
                <div style="margin-bottom: 8px;">
                  <strong style="color: #495057;">Status:</strong>
                  <span style="margin-left: 5px; padding: 4px 8px; border-radius: 4px; font-size: 12px; font-weight: bold; ${(item.Status !== undefined ? item.Status : item.status) ? 'background: #d4edda; color: #155724;' : 'background: #f8d7da; color: #721c24;'}">
                    ${(item.Status !== undefined ? item.Status : item.status) ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
              
              <div style="background: #f8f9fa; padding: 15px; border-radius: 8px; border-left: 4px solid #28a745;">
                <h6 style="color: #28a745; margin-bottom: 10px; font-weight: bold;">
                  <i class="bx bx-money" style="margin-right: 5px;"></i>
                  Financial Details
                </h6>
                <div style="margin-bottom: 8px;">
                  <strong style="color: #495057;">Cost:</strong>
                  <span style="color: #6c757d; margin-left: 5px;">${((item.Cost || item.cost) || 0).toFixed(2)}</span>
                </div>
                <div style="margin-bottom: 8px;">
                  <strong style="color: #495057;">Amount:</strong>
                  <span style="color: #6c757d; margin-left: 5px;">${((item.Amount || item.amount) || 0).toFixed(2)}</span>
                </div>
                <div style="margin-bottom: 8px;">
                  <strong style="color: #495057;">Discount Percentage:</strong>
                  <span style="color: #6c757d; margin-left: 5px;">${((item.DiscountPercentage || item.discountPercentage) || 0).toFixed(2)}%</span>
                </div>
                <div style="margin-bottom: 8px;">
                  <strong style="color: #495057; font-size: 16px;">Net Amount:</strong>
                  <span style="color: #28a745; margin-left: 5px; font-size: 16px; font-weight: bold;">${((item.NetAmount || item.netAmount) || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>
            
            <div style="background: #f8f9fa; padding: 15px; border-radius: 8px; border-left: 4px solid #ffc107; margin-bottom: 20px;">
              <h6 style="color: #ffc107; margin-bottom: 10px; font-weight: bold;">
                <i class="bx bx-list-check" style="margin-right: 5px;"></i>
                Services Included {/* UPDATED: Removed Products from display */}
              </h6>
              <div style="color: #6c757d; line-height: 1.6;">
                ${itemNames}
              </div>
            </div>
            
            <div style="background: #f8f9fa; padding: 15px; border-radius: 8px; border-left: 4px solid #dc3545;">
              <h6 style="color: #dc3545; margin-bottom: 10px; font-weight: bold;">
                <i class="bx bx-receipt" style="margin-right: 5px;"></i>
                Applied Taxes
              </h6>
              <div style="color: #6c757d; line-height: 1.6;">
                ${taxNames}
              </div>
            </div>
          </div>
          <div style="text-align: center; margin-top: 16px;">
            <button
              id="packageDetailsCloseBtn_${packageId}"
              type="button"
              style="
                background: #556ee6;
                color: white;
                border: none;
                padding: 10px 22px;
                border-radius: 6px;
                cursor: pointer;
                font-weight: 500;
                font-size: 14px;
                pointer-events: auto !important;
                z-index: 20001;
                position: relative;
              "
              onmouseover="this.style.background='#4858d4'"
              onmouseout="this.style.background='#556ee6'"
              onclick="if(window.__handleCloseClick_${packageId}) { window.__handleCloseClick_${packageId}(); } else { if(window.Swal) window.Swal.close(); }"
            >
              Close
            </button>
          </div>
        `,
        showConfirmButton: false,
        width: '800px',
        allowOutsideClick: false,
        allowEscapeKey: true,
        showClass: {
          popup: 'animate__animated animate__fadeInDown'
        },
        hideClass: {
          popup: 'animate__animated animate__fadeOutUp'
        },
        customClass: {
          container: 'swal2-top-layer',
          popup: 'swal2-custom-popup'
        },
        didOpen: () => {
          // Ensure SweetAlert is top-most so clicks don't "pass through"
          const container = Swal.getContainer?.();
          if (container) {
            container.style.zIndex = '20000';
          }

          // Create a robust close function that forces immediate close
          const forceCloseModal = () => {
            // Immediately try to close using Swal API
            try {
              Swal.close();
            } catch (e) {
              // Silently handle errors
            }
            
            // Safely remove SweetAlert elements
            const removeSwalElements = () => {
              try {
                // Get Swal elements using API
                const popup = Swal.getPopup?.();
                const container = Swal.getContainer?.();
                
                // Remove popup if it exists
                if (popup && popup.parentNode) {
                  popup.style.display = 'none';
                  popup.remove();
                }
                
                // Remove container if it exists
                if (container && container.parentNode) {
                  container.style.display = 'none';
                  container.remove();
                }
                
                // Remove backdrop/overlay - be more specific
                const backdrop = document.querySelector('.swal2-backdrop-show');
                if (backdrop && backdrop.parentNode) {
                  backdrop.style.display = 'none';
                  backdrop.remove();
                }
                
                // Also try the standard backdrop class
                const standardBackdrop = document.querySelector('.swal2-backdrop');
                if (standardBackdrop && standardBackdrop.parentNode && standardBackdrop !== backdrop) {
                  standardBackdrop.style.display = 'none';
                  standardBackdrop.remove();
                }
                
                // Safely remove body classes - check if body exists
                if (document.body) {
                  document.body.classList.remove('swal2-shown', 'swal2-height-auto', 'swal2-no-backdrop');
                  if (document.body.style) {
                    document.body.style.overflow = '';
                    document.body.style.paddingRight = '';
                  }
                }
              } catch (error) {
                // Silently handle errors
              }
            };
            
            // Execute immediately
            removeSwalElements();
            
            // Also execute after a short delay to catch any that appear
            setTimeout(removeSwalElements, 50);
            setTimeout(removeSwalElements, 150);
          };

          // Create global function for onclick fallback
          window[`__handleCloseClick_${packageId}`] = forceCloseModal;

          // Use setTimeout to ensure DOM is fully ready
          setTimeout(() => {
            // Attach click handler to the close button
            const closeBtn = document.getElementById(`packageDetailsCloseBtn_${packageId}`);
            
            if (closeBtn) {
              closeBtn.disabled = false;
              closeBtn.style.pointerEvents = 'auto';
              closeBtn.style.opacity = '1';
              closeBtn.style.cursor = 'pointer';
              closeBtn.style.zIndex = '20001';
              closeBtn.style.position = 'relative';
              
              // Create handler using the force close function
              const closeHandler = (e) => {
                e.preventDefault();
                e.stopPropagation();
                forceCloseModal();
              };
              
              // Remove any existing listener first
              if (closeBtn._closeHandler) {
                closeBtn.removeEventListener('click', closeBtn._closeHandler);
              }
              
              // Store handler reference for cleanup
              closeBtn._closeHandler = closeHandler;
              
              // Add event listener with capture to ensure it fires
              closeBtn.addEventListener('click', closeHandler, { capture: true });
              
              // Also try mousedown as fallback
              closeBtn.addEventListener('mousedown', (e) => {
                e.preventDefault();
                e.stopPropagation();
                forceCloseModal();
              });
            }
          }, 100);
        },
        didClose: () => {
          // Clean up global function
          if (window[`__handleCloseClick_${packageId}`]) {
            delete window[`__handleCloseClick_${packageId}`];
          }
          
          // Clean up event listeners when modal closes
          const closeBtn = document.getElementById(`packageDetailsCloseBtn_${packageId}`);
          if (closeBtn && closeBtn._closeHandler) {
            closeBtn.removeEventListener('click', closeBtn._closeHandler, { capture: true });
            delete closeBtn._closeHandler;
          }
        }
      });
    } catch (error) {
      console.error("Error viewing spa package:", error);
      Swal.fire("Error", "Failed to view spa package details", "error");
    }
  };

  const handleClear = () => {
    // Set default status based on permissions
    const defaultStatus = hasDeletePermission ? defaultForm.Status : true;
    setForm({ ...defaultForm, Status: defaultStatus });
    setServiceDetails([{ ...defaultServiceDetail }]);
    setSelectedTaxes([]);
    setIsEditing(false);
    setIsUsedInBilling(false);
    setOriginalServiceDetails([]);
    setFormKey(prev => prev + 1);
  };

  const handleApplyFilter = () => {
    setHasAppliedFilter(true);
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setHasAppliedFilter(true);
  };

  // Excel import functions
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) {
      return;
    }

    if (!file.name.match(/\.(xlsx|xls)$/)) {
      Swal.fire("Error", "Please select a valid Excel file (.xlsx or .xls)", "error");
      return;
    }

    setExcelFile(file);
    parseExcelFile(file);
  };

  const parseExcelFile = (file) => {
    // Check if services are loaded
    if (!spaServices || spaServices.length === 0) {
      Swal.fire("Error", "Please wait for services to load, then try again.", "error");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (jsonData.length === 0) {
          Swal.fire("Error", "No data found in the Excel file", "error");
          return;
        }

        // Helper function to get value from row (case-insensitive)
        const getValue = (row, keys) => {
          for (const key of keys) {
            if (row[key] !== undefined && row[key] !== null) {
              return row[key];
            }
          }
          return '';
        };

        // Group rows by PackageCode
        const packageMap = new Map();
        const invalidServiceCodes = new Set();

        jsonData.forEach((row, index) => {
          // Try multiple column name variations (case-insensitive)
          const packageCode = getValue(row, ['PackageCode', 'packagecode', 'Package Code', 'package code', 'PACKAGECODE']).toString().trim();
          const packageName = getValue(row, ['PackageName', 'packagename', 'Package Name', 'package name', 'PACKAGENAME']).toString().trim();
          const packagePrice = parseFloat(getValue(row, ['PackagePrice', 'packageprice', 'Package Price', 'package price', 'PACKAGEPRICE']) || 0);
          const packageCost = parseFloat(getValue(row, ['PackageCost', 'packagecost', 'Package Cost', 'package cost', 'PACKAGECOST']) || 0);
          const serviceCode = getValue(row, ['ServiceCode', 'servicecode', 'Service Code', 'service code', 'SERVICECODE']).toString().trim();
          const serviceQty = parseInt(getValue(row, ['ServiceQty', 'serviceqty', 'Service Qty', 'service qty', 'ServiceQty', 'SERVICEQTY']) || 1);

          // Skip empty rows
          if (!packageName && !packageCode && !serviceCode) {
            return;
          }

          // Get or create package
          const packageKey = packageCode || `AUTO_${index}`;
          if (!packageMap.has(packageKey)) {
            packageMap.set(packageKey, {
              PackageCode: packageCode || '', // Will be auto-generated if empty
              PackageName_En: packageName,
              PackageName_Ar: '', // Leave empty as per requirement
              Cost: packageCost > 0 ? packageCost : 0,
              Amount: packagePrice > 0 ? packagePrice : 0,
              NetAmount: packagePrice > 0 ? packagePrice : 0,
              DiscountAmount: 0,
              TaxAmount: 0,
              DiscountPercentage: 0,
              Status: true,
              CreatedBy: getUserSession().userId,
              ModifiedBy: getUserSession().userId,
              Details: [],
              TaxIds: [],
            });
          }

              // Add service detail if service code exists
          if (serviceCode) {
            // Map ServiceCode to ServiceId (case-insensitive)
            // Try multiple property name variations
            const service = spaServices.find(s => {
              const code = (s.SpaServiceCode || s.spaServiceCode || s.SpaService_Code || '').toString().trim();
              return code.toUpperCase() === serviceCode.toUpperCase();
            });

            if (service) {
              const serviceId = service.SpaService_Id || service.spaService_Id || service.SpaServiceId;
              const packageData = packageMap.get(packageKey);
              
              // Check if this service already exists in details (avoid duplicates)
              const existingDetail = packageData.Details.find(d => d.Service_Id === serviceId);
              if (existingDetail) {
                // Update quantity if same service
                existingDetail.Quantity = serviceQty > 0 ? serviceQty : 1;
              } else {
                packageData.Details.push({
                  Service_Id: serviceId,
                  // Product_Id: null, // COMMENTED OUT: Product functionality temporarily disabled
                  Quantity: serviceQty > 0 ? serviceQty : 1,
                });
              }
            } else {
              // Track invalid service code
              invalidServiceCodes.add(serviceCode);
            }
          }
        });

        // Convert map to array
        const mappedData = Array.from(packageMap.values());

        // Filter out packages with no valid service details
        const validPackages = mappedData.filter(pkg => pkg.Details.length > 0);
        const packagesWithoutServices = mappedData.filter(pkg => pkg.Details.length === 0);

        if (validPackages.length === 0) {
          Swal.fire("Error", "No valid packages found. Please ensure service codes are correct and match existing services.", "error");
          return;
        }

        // Show warnings if any
        let warningMessage = '';
        if (packagesWithoutServices.length > 0) {
          warningMessage += `${packagesWithoutServices.length} package(s) skipped (no valid services). `;
        }
        if (invalidServiceCodes.size > 0) {
          warningMessage += `${invalidServiceCodes.size} invalid service code(s) found: ${Array.from(invalidServiceCodes).join(', ')}. `;
        }

        if (warningMessage) {
          Swal.fire({
            icon: "warning",
            title: "Import Preview",
            text: warningMessage + `Found ${validPackages.length} valid package(s) to import.`,
            confirmButtonText: "Continue"
          });
        } else {
          Swal.fire("Success", `Found ${validPackages.length} package(s) to import`, "success");
        }

        setParsedData(validPackages);
        setImportResult({
          invalidServiceCodes: Array.from(invalidServiceCodes),
          packagesWithoutServices: packagesWithoutServices.map(p => p.PackageName_En || p.PackageCode),
        });
      } catch (error) {
        console.error("Error parsing Excel file:", error);
        Swal.fire("Error", "Failed to parse Excel file: " + error.message, "error");
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleBulkImport = async () => {
    if (parsedData.length === 0) {
      Swal.fire("Error", "No data to import", "error");
      return;
    }

    try {
      setImportLoading(true);
      setImportProgress({ current: 0, total: parsedData.length });

      const result = await bulkImportSpaPackages(parsedData);

      // Check if the response has ids (success) or error message
      if (result && (result.ids || result.message)) {
        let message = result.message || `Successfully imported ${parsedData.length} package(s)!`;
        let skippedInfo = '';
        let invalidCodesInfo = '';

        if (result.skippedPackages && result.skippedPackages.length > 0) {
          skippedInfo = `\n\nSkipped Packages:\n${result.skippedPackages.join('\n')}`;
        }

        // Combine invalid service codes from both backend and frontend parsing
        const allInvalidCodes = new Set();
        if (result.invalidServiceCodes && result.invalidServiceCodes.length > 0) {
          result.invalidServiceCodes.forEach(code => allInvalidCodes.add(code));
        }
        if (importResult && importResult.invalidServiceCodes.length > 0) {
          importResult.invalidServiceCodes.forEach(code => allInvalidCodes.add(code));
        }
        
        if (allInvalidCodes.size > 0) {
          invalidCodesInfo = `\n\nInvalid Service Codes:\n${Array.from(allInvalidCodes).join(', ')}`;
        }

        // Determine icon based on result
        const icon = result.ids && result.ids.length > 0 ? "success" : "warning";
        const title = result.ids && result.ids.length > 0 ? "Import Completed" : "Import Completed with Warnings";

        Swal.fire({
          icon: icon,
          title: title,
          html: `<div style="text-align: left;">${message}${skippedInfo}${invalidCodesInfo}</div>`,
          width: '600px',
        });
        
        // Clear the data and reload the list
        setParsedData([]);
        setExcelFile(null);
        setImportResult(null);
        setImportProgress({ current: 0, total: 0 });
        
        // Clear the file input element
        const fileInput = document.getElementById('excelFile');
        if (fileInput) {
          fileInput.value = '';
        }
        
        loadSpaPackages();
      } else {
        Swal.fire({
          icon: "error",
          title: "Import Failed",
          text: result.message || "Failed to import spa packages",
        });
      }
    } catch (error) {
      console.error("Error importing spa packages:", error);
      Swal.fire({
        icon: "error",
        title: "Import Error",
        text: error.response?.data?.message || "Failed to import spa packages. Please try again.",
      });
    } finally {
      setImportLoading(false);
      setImportProgress({ current: 0, total: 0 });
    }
  };

  const clearImportData = () => {
    setParsedData([]);
    setExcelFile(null);
    setImportResult(null);
    setImportProgress({ current: 0, total: 0 });
  };

  const handleExportExcel = async () => {
    try {
      setLoading(true);
      // Clean filter - remove empty strings and convert to proper format
      const cleanFilter = {
        ...(filter.PackageId !== null && filter.PackageId !== undefined && { PackageId: filter.PackageId }),
        ...(filter.PackageCode && filter.PackageCode.trim() !== '' && { PackageCode: filter.PackageCode }),
        ...(filter.PackageName_En && filter.PackageName_En.trim() !== '' && { PackageName_En: filter.PackageName_En }),
        ...(filter.Status !== null && filter.Status !== undefined && { Status: filter.Status })
      };
      
      await exportSpaPackagesToExcel(cleanFilter);
      Swal.fire("Success", "Spa package report exported successfully", "success");
    } catch (error) {
      console.error("Error exporting spa packages:", error);
      Swal.fire("Error", "Failed to export spa package report", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleExportDetails = async (packageId) => {
    try {
      setLoading(true);
      await exportSpaPackageDetailsToExcel(packageId);
      Swal.fire("Success", "Spa package details exported successfully", "success");
    } catch (error) {
      console.error("Error exporting spa package details:", error);
      Swal.fire("Error", "Failed to export spa package details", "error");
    } finally {
      setLoading(false);
    }
  };

  // Service detail management
  const addServiceDetail = () => {
    setServiceDetails([...serviceDetails, { ...defaultServiceDetail }]);
  };

  const removeServiceDetail = (index) => {
    if (serviceDetails.length > 1) {
      const newDetails = serviceDetails.filter((_, i) => i !== index);
      setServiceDetails(newDetails);
    }
  };

  const updateServiceDetail = (index, field, value) => {
    const newDetails = [...serviceDetails];
    newDetails[index] = { ...newDetails[index], [field]: value };
    setServiceDetails(newDetails);
  };

  const updateServiceDetailType = (index, newType) => {
    const newDetails = [...serviceDetails];
    newDetails[index] = { 
      ...newDetails[index], 
      ItemType: newType,
      // Clear the other type's selection when switching
      Service_Id: newType === "Service" ? newDetails[index].Service_Id : null,
      // Product_Id: newType === "Product" ? newDetails[index].Product_Id : null // COMMENTED OUT: Product functionality temporarily disabled
    };
    setServiceDetails(newDetails);
  };

  // Calculate pagination
  const totalPages = Math.ceil(totalCount / pageSize);
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = spaPackages.slice(startIndex, endIndex);

  // Options for dropdowns
  const serviceOptions = useMemo(() => {
    return spaServices.map(service => ({
      value: service.spaService_Id,
      label: service.spaServiceName_En
    }));
  }, [spaServices]);

  // COMMENTED OUT: Product functionality temporarily disabled
  // const productOptions = useMemo(() => {
  //   return products.map(product => ({
  //     value: product.product_Id,
  //     label: product.product_NameEn
  //   }));
  // }, [products]);

  // Get available service options for a specific dropdown (excluding already selected services)
  const getAvailableServiceOptions = (currentIndex) => {
    const selectedServiceIds = serviceDetails
      .map((detail, index) => index !== currentIndex ? detail.Service_Id : null)
      .filter(id => id !== null);
    
    return serviceOptions.filter(option => !selectedServiceIds.includes(option.value));
  };

  // COMMENTED OUT: Product functionality temporarily disabled
  // // Get available product options for a specific dropdown (excluding already selected products)
  // const getAvailableProductOptions = (currentIndex) => {
  //   const selectedProductIds = serviceDetails
  //     .map((detail, index) => index !== currentIndex ? detail.Product_Id : null)
  //     .filter(id => id !== null);
  //   
  //   return productOptions.filter(option => !selectedProductIds.includes(option.value));
  // };

  const taxOptions = useMemo(() => {
    return taxes.map(tax => ({
      value: tax.tax_Id,
      label: `${tax.tax_NameEn} (${tax.tax_Perc}%)`
    }));
  }, [taxes]);

  const statusOptions = [
    { value: true, label: "Active" },
    { value: false, label: "Inactive" }
  ];

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Pagination component (same style as SpaServicePage)
  const PaginationComponent = () => {
    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="text-muted">
          Showing {Math.min((page - 1) * pageSize + 1, totalCount)} to{" "}
          {Math.min(page * pageSize, totalCount)} of {totalCount} entries
        </div>
        <div className="pagination-wrapper">
          <Pagination
            className="ant-pagination"
            current={page}
            total={totalCount}
            pageSize={pageSize}
            onChange={handlePageChange}
            showTitle={false}
          />
        </div>
      </div>
    );
  };

  // Get service name by ID
  const getServiceName = (serviceId) => {
    const service = spaServices.find(s => s.spaService_Id === serviceId);
    return service ? service.spaServiceName_En : "N/A";
  };

  // COMMENTED OUT: Product functionality temporarily disabled
  // // Get product name by ID
  // const getProductName = (productId) => {
  //   const product = products.find(p => p.product_Id === productId);
  //   return product ? product.product_NameEn : "N/A";
  // };


  if (!hasPermission) {
    return null;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Spa Packages Management" breadcrumbItem="Spa" />

        <Row>
          <Col xs={12}>
            <Card>
              <CardBody className="p-0">
                <Nav tabs className="nav-tabs-custom">
                  {hasEditOrHigher && (
                    <NavItem>
                      <NavLink
                        style={{ 
                        cursor: "pointer",
                        fontWeight: activeTab === "1" ? "bold" : "normal",
                        color: activeTab === "1" ? "#556ee6" : "#74788d",
                        borderBottom: activeTab === "1" ? "2px solid #556ee6" : "none",
                        padding: "15px 25px",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                        className={activeTab === "1" ? "active" : ""}
                        onClick={() => setActiveTab("1")}
                      >
                        <i className="bx bx-plus-circle"></i>
                        <span>Add Package</span>
                      </NavLink>
                    </NavItem>
                  )}
                  <NavItem>
                    <NavLink
                      style={{ 
                        cursor: "pointer",
                        fontWeight: activeTab === "2" ? "bold" : "normal",
                        color: activeTab === "2" ? "#556ee6" : "#74788d",
                        borderBottom: activeTab === "2" ? "2px solid #556ee6" : "none",
                        padding: "15px 25px",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                      className={activeTab === "2" ? "active" : ""}
                      onClick={() => setActiveTab("2")}
                    >
                      <i className="bx bx-list-ul"></i>
                      <span>Packages List</span>
                    </NavLink>
                  </NavItem>
                  {hasEditOrHigher && (
                    <NavItem>
                      <NavLink
                        style={{ 
                          cursor: "pointer",
                          fontWeight: activeTab === "3" ? "bold" : "normal",
                          color: activeTab === "3" ? "#556ee6" : "#74788d",
                          borderBottom: activeTab === "3" ? "2px solid #556ee6" : "none",
                          padding: "15px 25px",
                          fontSize: "16px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px"
                        }}
                        className={activeTab === "3" ? "active" : ""}
                        onClick={() => setActiveTab("3")}
                      >
                        <Upload size={16} />
                        <span>Excel Import</span>
                      </NavLink>
                    </NavItem>
                  )}
                </Nav>
                <TabContent activeTab={activeTab} className="p-4">
                  <TabPane tabId="1">
                    <div>
                      {isUsedInBilling && (
                        <div className="alert alert-warning" role="alert">
                          <i className="bx bx-info-circle me-2"></i>
                          <strong>Limited Editing:</strong> This package has been used in billing. 
                          Only amount, discount, and tax can be modified. Package Code, Package Name, and Services cannot be changed. {/* UPDATED: Removed Products from message */}
                        </div>
                      )}
                      <Form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
                        {/* Row 1: Package Code * | Package Name (English) * */}
                        <Row>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="PackageCode">Package Code <span className="text-danger">*</span></Label>
                              <Input
                                id="PackageCode"
                                name="PackageCode"
                                type="text"
                                value={form.PackageCode}
                                onChange={(e) => setForm({ ...form, PackageCode: e.target.value })}
                                placeholder="Enter Package Code"
                                required
                                disabled={isUsedInBilling}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="PackageName_En">Package Name (English) <span className="text-danger">*</span></Label>
                              <Input
                                id="PackageName_En"
                                name="PackageName_En"
                                type="text"
                                value={form.PackageName_En}
                                onChange={(e) => setForm({ ...form, PackageName_En: e.target.value })}
                                placeholder="Enter Package Name in English"
                                required
                                disabled={isUsedInBilling}
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        {/* Row 2: Package Name (Arabic) */}
                        <Row>
                          <Col md={12}>
                            <FormGroup className="mb-4">
                              <Label for="PackageName_Ar">Package Name (Arabic)</Label>
                              <Input
                                id="PackageName_Ar"
                                name="PackageName_Ar"
                                type="text"
                                value={form.PackageName_Ar}
                                onChange={(e) => setForm({ ...form, PackageName_Ar: e.target.value })}
                                placeholder="Enter Package Name in Arabic"
                                disabled={isUsedInBilling}
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        {/* Row 3: Services/Products * */}
                        <Row>
                          <Col md={12}>
                            <Label className="fw-bold mb-3">Services <span className="text-danger">*</span></Label> {/* UPDATED: Removed Products from label */}
                            {serviceDetails.map((detail, index) => (
                              <Row key={index} className="mb-3 align-items-end">
                                <Col md={2}>
                                  <FormGroup>
                                    <Label>Type</Label>
                                    <Select
                                      key={`type-${index}-${formKey}-${detail.ItemType}`}
                                      value={{ value: "Service", label: "Service" }} // UPDATED: Always Service, removed Product option
                                      onChange={(selected) => {
                                        const newType = selected?.value || "Service";
                                        updateServiceDetailType(index, newType);
                                      }}
                                      options={[
                                        { value: "Service", label: "Service" },
                                        // { value: "Product", label: "Product" } // COMMENTED OUT: Product functionality temporarily disabled
                                      ]}
                                      placeholder="Select Type"
                                      styles={selectStyles}
                                      isDisabled={isUsedInBilling}
                                    />
                                  </FormGroup>
                                </Col>
                                <Col md={5}>
                                  <FormGroup>
                                    <Label>Service</Label> {/* UPDATED: Removed Product option */}
                                    {/* COMMENTED OUT: Product functionality temporarily disabled */}
                                    {/* {detail.ItemType === "Product" ? (
                                      <Select
                                        key={`product-${index}-${formKey}-${detail.ItemType}-${detail.Product_Id}`}
                                        value={productOptions.find(option => option.value === detail.Product_Id) || null}
                                        onChange={(selected) => updateServiceDetail(index, 'Product_Id', selected?.value || null)}
                                        options={getAvailableProductOptions(index)}
                                        placeholder="Select Product"
                                        styles={selectStyles}
                                        isClearable
                                        isSearchable
                                        maxMenuHeight={200}
                                        isDisabled={isUsedInBilling}
                                      />
                                    ) : ( */}
                                      <Select
                                        key={`service-${index}-${formKey}-${detail.ItemType}-${detail.Service_Id}`}
                                        value={serviceOptions.find(option => option.value === detail.Service_Id) || null}
                                        onChange={(selected) => updateServiceDetail(index, 'Service_Id', selected?.value || null)}
                                        options={getAvailableServiceOptions(index)}
                                        placeholder="Select Service"
                                        styles={selectStyles}
                                        isClearable
                                        isSearchable
                                        maxMenuHeight={200}
                                        isDisabled={isUsedInBilling}
                                      />
                                    {/* )} */}
                                  </FormGroup>
                                </Col>
                                <Col md={3}>
                                  <FormGroup>
                                    <Label>Quantity</Label>
                                    <Input
                                      type="number"
                                      min="1"
                                      value={detail.Quantity || ''}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        if (value === '' || parseInt(value) >= 1) {
                                          updateServiceDetail(index, 'Quantity', value === '' ? '' : parseInt(value));
                                        }
                                      }}
                                      placeholder="Quantity"
                                      disabled={isUsedInBilling}
                                    />
                                  </FormGroup>
                                </Col>
                                <Col md={2}>
                                  <div className="d-flex gap-2">
                                    {serviceDetails.length > 1 && (
                                      <Button
                                        type="button"
                                        color="danger"
                                        size="sm"
                                        onClick={() => removeServiceDetail(index)}
                                        disabled={isUsedInBilling}
                                      >
                                        <Minus size={16} />
                                      </Button>
                                    )}
                                    {index === serviceDetails.length - 1 && (
                                      <Button
                                        type="button"
                                        color="success"
                                        size="sm"
                                        onClick={addServiceDetail}
                                        disabled={isUsedInBilling}
                                      >
                                        <Plus size={16} />
                                      </Button>
                                    )}
                                  </div>
                                </Col>
                              </Row>
                            ))}
                          </Col>
                        </Row>

                        {/* Row 4: Cost Price | Sales Price * */}
                        <Row>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="Cost">Cost Price <span className="text-danger">*</span></Label>
                              <Input
                                id="Cost"
                                name="Cost"
                                type="number"
                                min="0.01"
                                step="0.01"
                                value={form.Cost !== null && form.Cost !== undefined ? form.Cost : ''}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setForm({ ...form, Cost: value === '' ? null : parseFloat(value) || null });
                                }}
                                placeholder="Enter Cost Price"
                                required
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="Amount">Sales Price <span className="text-danger">*</span></Label>
                              <Input
                                id="Amount"
                                name="Amount"
                                type="number"
                                min="0.01"
                                step="0.01"
                                value={form.Amount !== null && form.Amount !== undefined ? form.Amount : ''}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setForm({ ...form, Amount: value === '' ? null : parseFloat(value) || null });
                                }}
                                placeholder="Enter Sales Price"
                                required
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        {/* Row 5: Discount Percentage (%) | Taxes */}
                        <Row>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="DiscountPercentage">Discount Percentage (%)</Label>
                              <Input
                                id="DiscountPercentage"
                                name="DiscountPercentage"
                                type="number"
                                min="0"
                                max="100"
                                step="0.01"
                                value={form.DiscountPercentage !== null && form.DiscountPercentage !== undefined ? form.DiscountPercentage : ''}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setForm({ ...form, DiscountPercentage: value === '' ? null : parseFloat(value) || null });
                                }}
                                placeholder="Enter Discount %"
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="taxes">Taxes</Label>
                              <Select
                                value={selectedTaxes}
                                onChange={(selected) => setSelectedTaxes(selected || [])}
                                options={taxOptions}
                                placeholder="Select Taxes"
                                styles={selectStyles}
                                isMulti
                                isClearable
                                isSearchable
                                maxMenuHeight={200}
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        {/* Row 6: Net Amount | Active */}
                        <Row>
                          <Col md={6}>
                            <FormGroup className="mb-4">
                              <Label for="NetAmount">Net Amount</Label>
                              <Input
                                id="NetAmount"
                                name="NetAmount"
                                type="number"
                                min="0"
                                step="0.01"
                                value={form.Amount && form.Amount > 0 ? calculateNetAmount(form.Amount, form.DiscountPercentage || 0, selectedTaxes).toFixed(2) : ''}
                                placeholder="Calculated Net Amount"
                                readOnly
                                style={{ backgroundColor: '#f8f9fa', color: '#6c757d' }}
                              />
                            </FormGroup>
                          </Col>
                          {hasDeletePermission && (
                            <Col md={6}>
                              <FormGroup className="mb-4">
                                <Label for="Status" className="d-block mb-2">Status</Label>
                                <div className="form-check form-switch">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="Status"
                                    checked={form.Status}
                                    onChange={(e) => setForm({ ...form, Status: e.target.checked })}
                                  />
                                  <label className="form-check-label" htmlFor="Status">
                                    {form.Status ? "Active" : "Inactive"}
                                  </label>
                                </div>
                              </FormGroup>
                            </Col>
                          )}
                        </Row>

                        <Row className="mt-4">
                          <Col>
                            <div className="d-flex gap-2">
                              <Button
                                type="submit"
                                color="primary"
                                disabled={saveLoading || !form.PackageName_En.trim() || !form.PackageCode.trim()}
                                className="waves-effect waves-light"
                              >
                                {saveLoading && (
                                  <ClipLoader size={16} color="#ffffff" className="me-2" />
                                )}
                                {isEditing ? "Update Package" : "Save Package"}
                              </Button>
                              <Button
                                type="button"
                                color="secondary"
                                onClick={handleClear}
                                className="waves-effect waves-light"
                              >
                                Clear
                              </Button>
                              {isUsedInBilling && isEditing && (
                                <Button
                                  type="button"
                                  color="warning"
                                  onClick={() => {
                                    const restoredDetails = originalServiceDetails.map(detail => ({
                                      ItemType: "Service", // detail.Product_Id ? "Product" : "Service", // COMMENTED OUT: Product functionality temporarily disabled
                                      Service_Id: detail.Service_Id || null,
                                      // Product_Id: detail.Product_Id || null, // COMMENTED OUT: Product functionality temporarily disabled
                                      Quantity: detail.Quantity
                                    }));
                                    setServiceDetails(restoredDetails);
                                    Swal.fire("Info", "Service details restored to original selection", "info"); // UPDATED: Removed Products from message
                                  }}
                                  className="waves-effect waves-light"
                                  title="Restore original service selection" // UPDATED: Removed products from title
                                >
                                  <i className="bx bx-reset me-1"></i>
                                  Restore Items
                                </Button>
                              )}
                            </div>
                          </Col>
                        </Row>
                      </Form>
                    </div>
                  </TabPane>

                  <TabPane tabId="2">
                    <div>
                      <Card className="mb-4">
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center">
                            <h5 className="card-title mb-3">Filter Packages</h5>
                            <Button
                              style={{
                                backgroundColor: '#556ee6',
                                borderColor: '#556ee6',
                                color: 'white',
                                borderRadius: '6px',
                                padding: '8px 16px',
                                fontSize: '14px',
                                fontWeight: '500',
                                border: 'none',
                                cursor: 'pointer'
                              }}
                              onClick={() => setShowFilters(!showFilters)}
                            >
                              {showFilters ? "Hide Filters" : "Show Filters"}
                            </Button>
                          </div>
                          {showFilters && (
                            <Row className="g-3">
                              <Col md={4}>
                                <Label for="filterPackageName">Package Name</Label>
                                <Input
                                  id="filterPackageName"
                                  name="filterPackageName"
                                  type="text"
                                  value={filter.PackageName_En}
                                  onChange={(e) => setFilter({ ...filter, PackageName_En: e.target.value })}
                                  placeholder="Search package name..."
                                />
                              </Col>
                              <Col md={4}>
                                <Label for="filterPackageCode">Package Code</Label>
                                <Input
                                  id="filterPackageCode"
                                  name="filterPackageCode"
                                  type="text"
                                  value={filter.PackageCode}
                                  onChange={(e) => setFilter({ ...filter, PackageCode: e.target.value })}
                                  placeholder="Search package code..."
                                />
                              </Col>
                              <Col md={4}>
                                <Label for="filterStatus">Status</Label>
                                <Select
                                  value={statusOptions.find(option => option.value === filter.Status)}
                                  onChange={(selected) => setFilter({ ...filter, Status: selected?.value || null })}
                                  options={statusOptions}
                                  placeholder="Select Status"
                                  styles={selectStyles}
                                  isClearable
                                />
                              </Col>
                              <Col md={12} className="d-flex align-items-end">
                                <div className="d-flex gap-2">
                                  <Button
                                    color="primary"
                                    onClick={handleApplyFilter}
                                    className="waves-effect waves-light"
                                  >
                                    Apply Filter
                                  </Button>
                                  <Button
                                    color="secondary"
                                    onClick={handleClearFilter}
                                    className="waves-effect waves-light"
                                  >
                                    Clear Filter
                                  </Button>
                                  <Button
                                    color="info"
                                    onClick={() => {
                                      setBillingUsageMap({});
                                      loadSpaPackages();
                                    }}
                                    className="waves-effect waves-light"
                                    title="Refresh billing usage data"
                                  >
                                    <i className="bx bx-refresh me-1"></i>
                                    Refresh Billing
                                  </Button>
                                </div>
                              </Col>
                            </Row>
                          )}
                        </CardBody>
                      </Card>

                      <Card>
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="card-title mb-0">
                              Spa Packages List
                            </h5>
                            <div className="d-flex gap-2">
                              <Button
                                color="success"
                                onClick={handleExportExcel}
                                disabled={loading || totalCount === 0}
                                className="waves-effect waves-light"
                              >
                                <Download className="me-1" size={16} />
                                Export Excel
                              </Button>
                              {hasEditOrHigher && (
                                <Button
                                  color="primary"
                                  onClick={() => setActiveTab("1")}
                                  className="waves-effect waves-light"
                                >
                                  <i className="bx bx-plus me-1"></i>
                                  Add New Package
                                </Button>
                              )}
                            </div>
                          </div>
                          
                          {loading ? (
                            <div className="text-center p-4">
                              <ClipLoader size={50} color="#556ee6" />
                              <p className="mt-2">Loading spa packages...</p>
                            </div>
                          ) : (
                            <>
                              <div className="table-responsive">
                                <Table className="table table-bordered table-striped table-hover">
                                  <thead className="table-light">
                                    <tr>
                                      <th width="4%">#</th>
                                      <th width="10%">Package Code</th>
                                      <th width="15%">Package Name (En)</th>
                                      <th width="15%">Package Name (Ar)</th>
                                      <th width="8%">Cost</th>
                                      <th width="8%">Amount</th>
                                      <th width="8%">Discount %</th>
                                      <th width="8%">Net Amount</th>
                                      <th width="8%">Status</th>
                                      <th width="6%" title="Shows if package has been used in billing (limited editing)">Billing</th>
                                      <th width="10%">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {paginatedData.length > 0 ? (
                                      paginatedData.map((item, index) => (
                                        <tr key={item.PackageId || item.packageId}>
                                          <td>{startIndex + index + 1}</td>
                                          <td>{item.PackageCode || item.packageCode}</td>
                                          <td>{item.PackageName_En || item.packageName_En}</td>
                                          <td>{(item.PackageName_Ar || item.packageName_Ar) || 'N/A'}</td>
                                          <td className="text-end">{((item.Cost || item.cost) || 0).toFixed(2)}</td>
                                          <td className="text-end">{((item.Amount || item.amount) || 0).toFixed(2)}</td>
                                          <td className="text-end">{((item.DiscountPercentage || item.discountPercentage) || 0).toFixed(2)}%</td>
                                          <td className="text-end">{((item.NetAmount || item.netAmount) || 0).toFixed(2)}</td>
                                          <td>
                                            <span className={`badge ${(item.Status !== undefined ? item.Status : item.status) ? 'bg-success' : 'bg-danger'}`}>
                                              {(item.Status !== undefined ? item.Status : item.status) ? "Active" : "Inactive"}
                                            </span>
                                          </td>
                                          <td>
                                            {(() => {
                                              const usage = billingUsageMap[item.PackageId || item.packageId];
                                              console.log(`Package ${item.PackageId || item.packageId} billing usage:`, usage);
                                              if (usage === true) {
                                                return <span className="badge bg-warning text-dark">Used</span>;
                                              } else if (usage === false) {
                                                return <span className="badge bg-success">Available</span>;
                                              } else {
                                                return <span className="badge bg-secondary">Unknown</span>;
                                              }
                                            })()}
                                          </td>
                                          <td>
                                            <div className="d-flex gap-2">
                                              <Button
                                                color="info"
                                                size="sm"
                                                onClick={() => handleView(item)}
                                                className="waves-effect waves-light"
                                                title="View"
                                              >
                                                <Eye size={12} />
                                              </Button>
                                              <Button
                                                color="success"
                                                size="sm"
                                                onClick={() => handleExportDetails(item.PackageId || item.packageId)}
                                                disabled={loading}
                                                className="waves-effect waves-light"
                                                title="Export Details"
                                              >
                                                <Download size={12} />
                                              </Button>
                                              {hasEditOrHigher && (
                                                <Button
                                                  color="primary"
                                                  size="sm"
                                                  onClick={() => handleEdit(item)}
                                                  disabled={editLoadingRow === (item.PackageId || item.packageId)}
                                                  className="waves-effect waves-light"
                                                >
                                                  {editLoadingRow === (item.PackageId || item.packageId) ? (
                                                    <ClipLoader size={12} color="#ffffff" />
                                                  ) : (
                                                    <SquarePen size={12} />
                                                  )}
                                                </Button>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                      ))
                                    ) : (
                                      <tr>
                                        <td colSpan="11" className="text-center">
                                          {hasAppliedFilter ? "No spa packages found matching the filter criteria." : "No spa packages available. Please add some packages first."}
                                        </td>
                                      </tr>
                                    )}
                                  </tbody>
                                </Table>
                              </div>
                              
                              {/* Pagination */}
                              {totalCount > 0 && <PaginationComponent />}
                            </>
                          )}
                        </CardBody>
                      </Card>
                    </div>
                  </TabPane>

                  {hasEditOrHigher && (
                    <TabPane tabId="3">
                      <div>
                        <Row>
                          <Col xs={12}>
                            <Card>
                              <CardBody>
                                <h5 className="card-title mb-4">Excel Import</h5>
                                <p className="text-muted mb-4">
                                  Upload an Excel file to bulk import spa packages. The Excel file should contain columns: 
                                  <strong> PackageCode</strong>, <strong>PackageName</strong>, <strong>PackagePrice</strong>, 
                                  <strong> PackageCost</strong>, <strong>ServiceCode</strong>, and <strong>ServiceQty</strong>.
                                </p>
                                
                                <Row>
                                  <Col md={6}>
                                    <FormGroup>
                                      <Label for="excelFile">Select Excel File</Label>
                                      <Input
                                        id="excelFile"
                                        type="file"
                                        accept=".xlsx,.xls"
                                        onChange={handleFileUpload}
                                        className="form-control"
                                      />
                                      <small className="form-text text-muted">
                                        Supported formats: .xlsx, .xls
                                      </small>
                                    </FormGroup>
                                  </Col>
                                </Row>

                                {parsedData.length > 0 && (
                                  <div className="mb-4">
                                    <h6>Preview ({parsedData.length} package(s) found):</h6>
                                    <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                      <Table className="table table-bordered table-striped table-sm">
                                        <thead className="table-dark">
                                          <tr>
                                            <th>#</th>
                                            <th>Package Code</th>
                                            <th>Package Name</th>
                                            <th>Cost</th>
                                            <th>Price (Net Amount)</th>
                                            <th>Services Count</th>
                                            <th>Services</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {parsedData.slice(0, 10).map((pkg, index) => (
                                            <tr key={index}>
                                              <td>{index + 1}</td>
                                              <td>{pkg.PackageCode || '(Auto-generate)'}</td>
                                              <td>{pkg.PackageName_En}</td>
                                              <td>{pkg.Cost.toFixed(2)}</td>
                                              <td>{pkg.NetAmount.toFixed(2)}</td>
                                              <td>{pkg.Details.length}</td>
                                              <td>
                                                <small>
                                                  {pkg.Details.map((detail, idx) => {
                                                    const service = spaServices.find(s => 
                                                      (s.SpaService_Id || s.spaService_Id) === detail.Service_Id
                                                    );
                                                    return service 
                                                      ? `${service.SpaServiceName_En || service.spaServiceName_En} (Qty: ${detail.Quantity})`
                                                      : `Service ID: ${detail.Service_Id} (Qty: ${detail.Quantity})`;
                                                  }).join(', ')}
                                                </small>
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </Table>
                                    </div>
                                  </div>
                                )}

                                {parsedData.length > 10 && (
                                  <div className="mt-4 mb-4">
                                    <p className="text-muted mb-0">
                                      Showing first 10 packages. Total: {parsedData.length} packages
                                    </p>
                                  </div>
                                )}

                                {importResult && importResult.invalidServiceCodes.length > 0 && (
                                  <div className="alert alert-warning mb-4">
                                    <strong>Invalid Service Codes Found:</strong>
                                    <ul className="mb-0 mt-2">
                                      {importResult.invalidServiceCodes.map((code, idx) => (
                                        <li key={idx}>{code}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}

                                <div className="d-flex gap-2">
                                  <Button
                                    color="success"
                                    onClick={handleBulkImport}
                                    disabled={parsedData.length === 0 || importLoading}
                                    className="waves-effect waves-light"
                                  >
                                    {importLoading ? (
                                      <>
                                        <ClipLoader size={16} color="#ffffff" className="me-2" />
                                        Importing...
                                      </>
                                    ) : (
                                      <>
                                        <i className="bx bx-upload me-2"></i>
                                        Import {parsedData.length} Package(s)
                                      </>
                                    )}
                                  </Button>
                                  
                                  {parsedData.length > 0 && (
                                    <Button
                                      color="secondary"
                                      onClick={clearImportData}
                                      disabled={importLoading}
                                      className="waves-effect waves-light"
                                    >
                                      Clear
                                    </Button>
                                  )}
                                </div>

                                {importProgress.total > 0 && (
                                  <div className="mt-3">
                                    <div className="progress">
                                      <div 
                                        className="progress-bar" 
                                        role="progressbar" 
                                        style={{ width: `${(importProgress.current / importProgress.total) * 100}%` }}
                                      >
                                        {importProgress.current} / {importProgress.total}
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </CardBody>
                            </Card>
                          </Col>
                        </Row>
                      </div>
                    </TabPane>
                  )}
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
